import { memo, useCallback, useMemo, useState } from 'react';
import * as Sentry from '@sentry/node';
import TariffCard, { ITvDescriptions } from '@r1-frontend/ui-react/components/TariffCard';
import { isClientSide } from '@r1-frontend/shared/helpers/ssr';
import { dataLayerPush } from '@r1-frontend/ui-react/components/helpers';

import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { monthPluralize, channelPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import { useWebp } from '~/src/helpers/hooks/useWebp';
import { useProgressiveImage } from '~/src/helpers/hooks/useProgressiveImage';
import { EVENTS, ACTIONS, CATEGORIES } from '~/src/constants/analytics';

import { Wrapper } from './styled';

const monthText = getPluralize(monthPluralize);
const channelText = getPluralize(channelPluralize);

export const MAX_ADV_QUANTITY = 2;

const TariffCardWrapper = ({
                        tariff,
                        openModal = () => ({}),
                        tiled = false,
                        idForScroll,
                        isFullByActive = false,
                        buttonProps,
                        cityDomain,
                    }): JSX.Element => {
    const {
        alias,
        advantages,
        hdCount = 0,
        imageBackground,
        imageBackgroundWebp,
        includes,
        isCottage,
        offerContents,
        priceAction,
        priceActionPeriod,
        sdCount = 0,
        title,
        isNeedRequest,
        type,
    } = tariff;
    // Сделали landingAvailable=1 костыль, для того, чтобы появлялись тарифы "Доступен на лендингах"
    // Как работает - в full-buy считываем параметр и передаем в запрос
    const fullBuyPath =
        `https://${cityDomain}.dom.ru/full-buy?tariffName=${alias}&monoTariff=${type === 'internet'}&landingAvailable=1${isCottage ? '&filters=cottage' : '' }`;

    const hasUrlBackground = useMemo(() => !!(imageBackgroundWebp?.includes('url') || imageBackground?.includes('url')),
        [imageBackground, imageBackgroundWebp]);
    const isWebpUsed = useWebp();

    const resultBackground = (isWebpUsed && imageBackgroundWebp) ? imageBackgroundWebp : imageBackground;

    const background = useProgressiveImage(
        hasUrlBackground ? resultBackground && resultBackground.replace(/^url|[()]/g, '') : resultBackground,
        { hasTimeout: true, timeoutDelay: 10 },
    );

    const [isOpenInternet, setIsOpenInternet] = useState(true);

    const promoPeriod = useMemo(
        () => priceActionPeriod ? `${priceActionPeriod} ${monthText(priceActionPeriod)}` : '',
        [priceActionPeriod],
    );

    const withSubscriptions = useMemo(() => !!offerContents?.length, [offerContents?.length]);

    const internetData = useMemo<string[]>(() => {
        const data = advantages?.length
            ? advantages
                .slice(0, MAX_ADV_QUANTITY)
                .map(advantage => advantage.description)
            : [];
        return data;
    }, [advantages]);

    const tvData = useMemo<ITvDescriptions[]>(() => {
        const data = [];

        includes?.length && data.push({
            desc: includes[0].description,
        });
        (hdCount || sdCount) && data.push({
            desc: hdCount
                ? `${hdCount}&nbsp;HD&nbsp;${channelText(hdCount)}.&nbsp;`
                : `${sdCount}&nbsp;${channelText(sdCount)}.&nbsp;`,
            details: 'Подробнее',
        });

        return data;
    }, [hdCount, includes, sdCount]);

    const buttonText = useMemo(() => {
        return isNeedRequest
            ? 'Создать заявку'
            : (priceAction
                ? 'Подключить со скидкой'
                : 'Подключить тариф');
    }, [priceAction, isNeedRequest]);

    const onToggleHandler = useCallback(() => setIsOpenInternet(!isOpenInternet), [isOpenInternet]);

    const openFullBuyHref = useCallback(() => {
        if (buttonProps.isTargetBlank) {
            window.open(fullBuyPath, '_blank', 'noopener');
        } else {
            window.location.href = fullBuyPath;
        }
    }, [buttonProps.isTargetBlank, fullBuyPath]);

    const onClickHandler = useCallback(() => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.tariffCards,
            action: ACTIONS.clickChooseTariff,
            label: isFullByActive ? `fullBy|${title}` : `callback|${title}`,
        });

        if (isFullByActive && isClientSide()) {
            openFullBuyHref();
        } else if (idForScroll) {
            try {
                const elem = document.querySelector(`#${idForScroll}`);
                elem?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            } catch (err) {
                Sentry.captureException(err);
        }
    }
    }, [isFullByActive, title, idForScroll, openFullBuyHref]);

    return (
        <Wrapper>
            <TariffCard
                tariff={tariff}
                openModal={openModal}
                tiled={tiled}
                background={background}
                hasUrlBackground={hasUrlBackground}
                withSubscriptions={withSubscriptions}
                tvData={tvData}
                internetData={internetData}
                promoPeriod={promoPeriod}
                buttonText={buttonProps.buttonText || buttonText}
                onClickHandler={onClickHandler}
                buyTypePopup={<></>}
                isOpenInternet={isOpenInternet}
                onToggleHandler={onToggleHandler}
            />
        </Wrapper>
    );
};

export default memo(TariffCardWrapper);
